import { z } from 'zod';

/**
 * Contrats partagés avec le backend.
 * Le backend (Express) n'expose pas d'OpenAPI : ces schémas sont la source de
 * vérité côté front. Si un jour le backend publie un swagger, on génère ce
 * fichier au lieu de le maintenir à la main.
 *
 * Deux populations distinctes, deux tables, deux jetons :
 * - `User`  : membre d'un cabinet, app.fidalia.fr, code à usage unique
 * - `Admin` : administrateur plateforme, admin.fidalia.fr, email + mot de passe
 */

/** Rôle **au sein du cabinet**. `membre` = compte secondaire. */
export const roleSchema = z.enum(['admin', 'membre']);
export type Role = z.infer<typeof roleSchema>;

export const fonctionSchema = z.enum(['praticien', 'assistant', 'secretariat', 'gestionnaire']);
export type Fonction = z.infer<typeof fonctionSchema>;

export const exerciceSchema = z.enum(['seul', 'cabinet']);
export type Exercice = z.infer<typeof exerciceSchema>;

export const effectifSchema = z.enum(['2-5', '6-10', '11+']);
export type Effectif = z.infer<typeof effectifSchema>;

export const cabinetStatutSchema = z.enum(['essai', 'actif', 'impaye', 'resilie']);
export type CabinetStatut = z.infer<typeof cabinetStatutSchema>;

/**
 * Rôle **plateforme**. Sans rapport avec `roleSchema`.
 * `superadmin` gouverne les administrateurs eux-mêmes.
 */
export const adminRoleSchema = z.enum(['superadmin', 'admin']);
export type AdminRole = z.infer<typeof adminRoleSchema>;

export const userSchema = z.object({
  id: z.string(),
  cabinetId: z.string(),
  prenom: z.string(),
  nom: z.string(),
  email: z.email(),
  indicatif: z.string(),
  telephone: z.string(),
  fonction: fonctionSchema,
  role: roleSchema,
  actif: z.boolean(),
  /** Compte fondateur du cabinet : ni supprimable, ni désactivable. */
  principal: z.boolean(),
  emailVerifie: z.boolean(),
  onboardingFait: z.boolean(),
  /**
   * Compte Doctolib branché à l'extension de ce membre, et date de sa
   * dernière synchronisation aboutie. `null` tant qu'il n'a jamais
   * synchronisé — c'est ainsi qu'on distingue, dans la liste des accès, ceux
   * qui alimentent Fidalia de ceux qui n'ont pas encore installé l'extension.
   */
  // `z.string()` et non `z.email()` : cette adresse vient de Doctolib, pas
  // d'une saisie Fidalia. Un format inattendu de leur côté ne doit pas faire
  // échouer la validation de toute la liste des accès.
  doctolibEmail: z.string().nullable(),
  doctolibNom: z.string().nullable(),
  doctolibSyncAt: z.iso.datetime().nullable(),
  /** Auteur du compte. `null` pour le fondateur du cabinet. */
  createdById: z.string().nullable(),
  lastLoginAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type User = z.infer<typeof userSchema>;

/** Formule payante (l'essai gratuit n'a pas de Price Stripe associé). */
export const formuleSchema = z.enum(['solo', 'cabinet', 'centre']);
export type Formule = z.infer<typeof formuleSchema>;

export const periodeSchema = z.enum(['mensuel', 'annuel']);
export type Periode = z.infer<typeof periodeSchema>;

export const cabinetSchema = z.object({
  id: z.string(),
  nom: z.string(),
  exercice: exerciceSchema,
  effectif: effectifSchema.nullable(),
  statut: cabinetStatutSchema,
  /** `false` : accès coupé par le back-office, tous comptes du cabinet. */
  actif: z.boolean(),
  /** Formule Gold accordée par le back-office : tout en illimité, hors Stripe. */
  gold: z.boolean(),
  finEssai: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  /** Formule et période de l'abonnement Stripe en cours. `null` en essai. */
  formule: formuleSchema.nullable(),
  periode: periodeSchema.nullable(),
  /** Fin de la période en cours (prochain renouvellement, ou fin d'accès). */
  periodeFin: z.iso.datetime().nullable(),
  /** `true` si l'abonnement s'arrêtera à `periodeFin` sans se renouveler. */
  annulationProgrammee: z.boolean(),
  /** Rétrogradation qui prendra effet à `periodeFin`. `null` sans changement
   * en attente. */
  formuleProgrammee: formuleSchema.nullable(),
  periodeProgrammee: periodeSchema.nullable(),
});
export type Cabinet = z.infer<typeof cabinetSchema>;

export const adminSchema = z.object({
  id: z.string(),
  prenom: z.string(),
  nom: z.string(),
  email: z.email(),
  role: adminRoleSchema,
  actif: z.boolean(),
  lastLoginAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type Admin = z.infer<typeof adminSchema>;

/* ------------------------------------------------------------------ */
/* Réponses                                                            */
/* ------------------------------------------------------------------ */

/** `POST /auth/code/request` : toujours `true`, même si le compte n'existe pas. */
export const codeSentSchema = z.object({ sent: z.literal(true) });
export type CodeSent = z.infer<typeof codeSentSchema>;

/** `POST /auth/code/verify` : le jeton n'existe qu'ici. */
export const sessionSchema = z.object({
  token: z.string(),
  user: userSchema,
  cabinet: cabinetSchema,
});
export type Session = z.infer<typeof sessionSchema>;

/** `GET /auth/me` et `POST /cabinets/onboarding`. */
export const meSchema = z.object({
  user: userSchema,
  cabinet: cabinetSchema,
});
export type Me = z.infer<typeof meSchema>;

/** `POST /auth/register` : pas de jeton, il faut d'abord vérifier le code. */
export const registerResultSchema = meSchema;
export type RegisterResult = z.infer<typeof registerResultSchema>;

export const adminSessionSchema = z.object({
  token: z.string(),
  admin: adminSchema,
});
export type AdminSession = z.infer<typeof adminSessionSchema>;

export const adminMeSchema = z.object({ admin: adminSchema });
export type AdminMe = z.infer<typeof adminMeSchema>;

export const adminListSchema = z.object({
  items: z.array(adminSchema),
  total: z.number().int().nonnegative(),
});
export type AdminList = z.infer<typeof adminListSchema>;

/**
 * `POST /admin/admins` : le mot de passe en clair n'est renvoyé que s'il a été
 * généré, et **une seule fois**. Seul son hash est conservé.
 */
export const adminCreatedSchema = z.object({
  admin: adminSchema,
  motDePasseGenere: z.string().nullable(),
});
export type AdminCreated = z.infer<typeof adminCreatedSchema>;

export const adminResultSchema = z.object({ admin: adminSchema });
export type AdminResult = z.infer<typeof adminResultSchema>;

export const userListSchema = z.object({
  items: z.array(userSchema),
  total: z.number().int().nonnegative(),
});
export type UserList = z.infer<typeof userListSchema>;

export const userResultSchema = z.object({ user: userSchema });
export type UserResult = z.infer<typeof userResultSchema>;

/**
 * `GET /admin/users` : vue plateforme. Une ligne par **compte responsable** de
 * cabinet ; les accès secondaires sont résumés par `accesSecondaires`.
 */
export const adminUserSchema = userSchema.extend({
  cabinet: cabinetSchema,
  accesSecondaires: z.number().int().nonnegative(),
  /** Dernier relevé Doctolib du cabinet ; `null` = jamais synchronisé. */
  syncAt: z.iso.datetime().nullable(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;

export const adminUserListSchema = z.object({
  items: z.array(adminUserSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
});
export type AdminUserList = z.infer<typeof adminUserListSchema>;

/** `PATCH /admin/cabinets/:id/actif` : ouverture ou coupure de l'accès. */
export const cabinetResultSchema = z.object({ cabinet: cabinetSchema });
export type CabinetResult = z.infer<typeof cabinetResultSchema>;

/** `PATCH /admin/cabinets/:id/gold` : accorde ou retire la formule Gold. */
export const setCabinetGoldInputSchema = z.object({ gold: z.boolean() });
export type SetCabinetGoldInput = z.infer<typeof setCabinetGoldInputSchema>;

/* ------------------------------------------------------------------ */
/* Doctolib                                                            */
/* ------------------------------------------------------------------ */

/**
 * Ce que l'extension a relevé dans l'espace pro Doctolib du cabinet.
 *
 * Miroir des types de l'extension (`src/shared/types.ts` du dépôt
 * `fidalia-extension`) et de ceux du backend (`src/types/Doctolib.ts`) : les
 * trois décrivent la même charge et doivent le rester.
 */

/**
 * Nature d'un agenda, donnée par Doctolib et jamais déduite du nom : seuls les
 * agendas `praticien` correspondent à une personne, les autres sont des
 * plannings de matériel partagé ou de créneaux administratifs.
 */
export const natureAgendaSchema = z.enum(['praticien', 'equipement', 'ressource']);
export type NatureAgenda = z.infer<typeof natureAgendaSchema>;

/**
 * Rappels que Doctolib envoie déjà de son côté pour cet agenda.
 *
 * Décisif avant de programmer un message Fidalia : sur un agenda où Doctolib
 * envoie confirmation, deux relances email et un SMS, un message de plus
 * relève du harcèlement. Sur un agenda où tout est coupé, Fidalia est le seul
 * lien avec le patient. Ces réglages varient d'un agenda à l'autre.
 */
export const rappelsDoctolibSchema = z.object({
  /** Accusé de prise de rendez-vous. */
  confirmation: z.boolean(),
  emailPremier: z.boolean(),
  emailDernier: z.boolean(),
  sms: z.boolean(),
  /** Demande d'avis après la consultation. */
  avis: z.boolean(),
});
export type RappelsDoctolib = z.infer<typeof rappelsDoctolibSchema>;

export const agendaDoctolibSchema = z.object({
  id: z.number().int(),
  nom: z.string(),
  nature: natureAgendaSchema,
  praticienId: z.number().int().nullable(),
  rappels: rappelsDoctolibSchema,
});
export type AgendaDoctolib = z.infer<typeof agendaDoctolibSchema>;

export const categorieDoctolibSchema = z.object({
  id: z.number().int(),
  nom: z.string(),
  /** Ordre voulu par le cabinet, celui que voit le patient chez Doctolib. */
  position: z.number().int(),
});
export type CategorieDoctolib = z.infer<typeof categorieDoctolibSchema>;

export const motifDoctolibSchema = z.object({
  id: z.number().int(),
  nom: z.string(),
  categorieId: z.number().int().nullable(),
  /** Durée du créneau par défaut, en minutes. */
  dureeMin: z.number().int().nullable(),
  teleconsultation: z.boolean(),
  /**
   * Teinte donnée au motif dans le planning Doctolib (`#rrggbb`), ou `null`.
   * Calibrée pour des blocs d'agenda, pas pour une interface : à n'utiliser
   * qu'en pastille décorative, jamais en texte ni en fond.
   */
  couleur: z.string().nullable(),
  /**
   * Agendas où le motif est effectivement proposé. Doctolib empile une
   * configuration par défaut et des surcharges par agenda, dont certaines le
   * désactivent — d'où des écarts réels d'un praticien à l'autre.
   */
  agendaIds: z.array(z.number().int()),
});
export type MotifDoctolib = z.infer<typeof motifDoctolibSchema>;

/** Qui a lancé la synchronisation. `null` si son accès a été supprimé depuis. */
export const auteurReleveSchema = z.object({
  id: z.string(),
  prenom: z.string(),
  nom: z.string(),
  fonction: fonctionSchema,
});
export type AuteurReleve = z.infer<typeof auteurReleveSchema>;

export const releveDoctolibSchema = z.object({
  /** Organisation Doctolib : le cabinet tel que *Doctolib* le nomme. */
  organisationId: z.number().int().nullable(),
  organisationNom: z.string().nullable(),
  /** Le compte Doctolib d'où vient le relevé. */
  compte: z.object({
    nom: z.string().nullable(),
    email: z.string().nullable(),
    /**
     * Seule image de toute la réponse Doctolib, et le plus souvent leur
     * silhouette générique (`/avatars/default_…`) : écartée à l'affichage au
     * profit des initiales, qui en disent davantage.
     */
    avatar: z.string().nullable(),
  }),
  agendas: z.array(agendaDoctolibSchema),
  categories: z.array(categorieDoctolibSchema),
  motifs: z.array(motifDoctolibSchema),
  /**
   * Le cabinet dont la fiche est publique sur Doctolib — nom et adresse, de
   * quoi reconstruire son lien public. `null` si Doctolib n'en rapporte
   * aucun, ou si nom ou ville manque.
   */
  practice: z
    .object({
      nom: z.string(),
      adresse: z.string().nullable(),
      codePostal: z.string().nullable(),
      ville: z.string(),
      /**
       * Fixe du cabinet, celui de sa fiche publique — et non celui du compte
       * praticien. C'est lui que la signature par défaut fait lire au patient.
       */
      telephone: z.string().nullable(),
    })
    .nullable(),
  releveLe: z.iso.datetime(),
  relevePar: auteurReleveSchema.nullable(),
});
export type ReleveDoctolib = z.infer<typeof releveDoctolibSchema>;

/**
 * `GET /doctolib`. `releve: null` n'est pas une erreur : c'est l'état d'un
 * cabinet qui n'a pas encore synchronisé, et l'app en fait un écran d'accueil.
 */
export const releveDoctolibResultSchema = z.object({ releve: releveDoctolibSchema.nullable() });
export type ReleveDoctolibResult = z.infer<typeof releveDoctolibResultSchema>;

/** Membre dont la dernière synchronisation a écrit une entrée du catalogue. */
export const importateurDoctolibSchema = z.object({
  id: z.string(),
  prenom: z.string(),
  nom: z.string(),
  email: z.email(),
});
export type ImportateurDoctolib = z.infer<typeof importateurDoctolibSchema>;

/**
 * `GET /admin/doctolib/synchronisations`. Qui a synchronisé quel compte
 * Doctolib, et quand — un membre par ligne, pas un cabinet.
 */
export const synchronisationDoctolibSchema = z.object({
  userId: z.string(),
  prenom: z.string(),
  nom: z.string(),
  /** Adresse du compte Fidalia — pas celle de Doctolib. */
  email: z.email(),
  cabinetId: z.string(),
  cabinetNom: z.string(),
  doctolibEmail: z.string().nullable(),
  doctolibNom: z.string().nullable(),
  doctolibSyncAt: z.iso.datetime(),
  /**
   * Lien public du cabinet sur Doctolib, reconstruit depuis son nom et sa
   * ville. `null` tant que le cabinet n'a jamais synchronisé ses agendas, ou
   * que sa fiche n'a pas de nom/ville exploitable.
   */
  lienDoctolib: z.string().nullable(),
});
export type SynchronisationDoctolib = z.infer<typeof synchronisationDoctolibSchema>;

export const synchronisationDoctolibListSchema = z.object({
  items: z.array(synchronisationDoctolibSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
});
export type SynchronisationDoctolibList = z.infer<typeof synchronisationDoctolibListSchema>;

/**
 * `GET /admin/doctolib/specialites`. Catalogue des spécialités Doctolib
 * jamais vues, tous cabinets confondus — alimenté à chaque synchronisation,
 * jamais purgé. `cabinetsCount` retombe à zéro plutôt que de faire
 * disparaître la ligne quand plus aucun cabinet ne la déclare.
 */
export const specialiteDoctolibSchema = z.object({
  id: z.number().int(),
  nom: z.string(),
  /** `kind` côté Doctolib : `medical`, `paramedical`… */
  genre: z.string().nullable(),
  slug: z.string().nullable(),
  /** `data_sub_group` côté Doctolib. */
  groupe: z.string().nullable(),
  rappelAdapte: z.boolean(),
  premiereVueLe: z.iso.datetime(),
  derniereVueLe: z.iso.datetime(),
  cabinetsCount: z.number().int().nonnegative(),
  /** `null` si son accès a été supprimé depuis. */
  dernierImportPar: importateurDoctolibSchema.nullable(),
});
export type SpecialiteDoctolib = z.infer<typeof specialiteDoctolibSchema>;

export const specialiteDoctolibListSchema = z.object({
  items: z.array(specialiteDoctolibSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
});
export type SpecialiteDoctolibList = z.infer<typeof specialiteDoctolibListSchema>;

/**
 * `GET /admin/doctolib/motifs`. Catalogue des motifs de consultation Doctolib
 * jamais vus, keyé sur le nom — les identifiants Doctolib des motifs sont
 * propres à chaque cabinet, seul le libellé se compare d'un compte à l'autre.
 */
export const motifDoctolibCatalogueSchema = z.object({
  id: z.string(),
  nom: z.string(),
  /**
   * Identifiant et teinte du dernier cabinet à avoir rapporté ce motif —
   * propres à ce compte, pas une clé stable : deux cabinets peuvent donner un
   * identifiant et une couleur différents au même intitulé. Un exemple, pas
   * une référence.
   */
  doctolibId: z.number().int().nullable(),
  couleur: z.string().nullable(),
  premiereVueLe: z.iso.datetime(),
  derniereVueLe: z.iso.datetime(),
  /** `null` si son accès a été supprimé depuis. */
  dernierImportPar: importateurDoctolibSchema.nullable(),
});
export type MotifDoctolibCatalogue = z.infer<typeof motifDoctolibCatalogueSchema>;

export const motifDoctolibCatalogueListSchema = z.object({
  items: z.array(motifDoctolibCatalogueSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
});
export type MotifDoctolibCatalogueList = z.infer<typeof motifDoctolibCatalogueListSchema>;

/**
 * Spécialité déclarée par le dernier compte à avoir synchronisé un cabinet —
 * même forme que `specialiteDoctolibSchema` sans les champs propres au
 * catalogue (`cabinetsCount`, `dernierImportPar`), hors de propos sur une
 * fiche qui ne parle que d'un seul cabinet.
 */
export const specialiteDoctolibDetailSchema = z.object({
  id: z.number().int(),
  nom: z.string(),
  genre: z.string().nullable(),
  slug: z.string().nullable(),
  groupe: z.string().nullable(),
  rappelAdapte: z.boolean(),
});
export type SpecialiteDoctolibDetail = z.infer<typeof specialiteDoctolibDetailSchema>;

/**
 * `GET /admin/users/:id` : fiche complète. `abonnements` est toujours vide :
 * la facturation n'est pas encore modélisée, le champ réserve sa place.
 */
export const adminUserDetailSchema = z.object({
  user: userSchema,
  cabinet: cabinetSchema,
  accesSecondaires: z.array(userSchema),
  abonnements: z.array(z.never()),
  doctolib: z.object({
    /** Dernier relevé du cabinet. `null` tant qu'aucune synchronisation n'a abouti. */
    releve: releveDoctolibSchema.nullable(),
    specialite: specialiteDoctolibDetailSchema.nullable(),
    /** Lien public de la fiche Doctolib, reconstruit depuis `releve.practice`. */
    lienDoctolib: z.string().nullable(),
  }),
});
export type AdminUserDetail = z.infer<typeof adminUserDetailSchema>;

/* ------------------------------------------------------------------ */
/* Entrées                                                             */
/* ------------------------------------------------------------------ */

const emailInput = z.email("Format d'email invalide");
const phoneInput = z.string().regex(/^\d[\d\s().-]{5,}$/, 'Numéro de téléphone invalide');

export const requestCodeInputSchema = z.object({ email: emailInput });
export type RequestCodeInput = z.infer<typeof requestCodeInputSchema>;

export const verifyCodeInputSchema = z.object({
  email: emailInput,
  code: z.string().length(6, 'Le code fait 6 chiffres'),
});
export type VerifyCodeInput = z.infer<typeof verifyCodeInputSchema>;

/** `POST /admin/auth/login` : le back-office n'utilise pas de code par mail. */
export const adminLoginInputSchema = z.object({
  email: emailInput,
  password: z.string().min(1, 'Renseignez votre mot de passe'),
});
export type AdminLoginInput = z.infer<typeof adminLoginInputSchema>;

/**
 * `POST /auth/register/google` : inscription terminée après un OAuth.
 * **Pas de champ `email`** : il vit dans le ticket signé, côté serveur. Le
 * front ne peut donc pas le substituer.
 */
const passwordInput = z.string().min(12, 'Le mot de passe fait 12 caractères minimum');

/** `POST /admin/admins`. Sans mot de passe, le backend en génère un. */
export const createAdminInputSchema = z.object({
  prenom: z.string().trim().min(2, 'Indiquez le prénom.'),
  nom: z.string().trim().min(2, 'Indiquez le nom.'),
  email: emailInput,
  role: adminRoleSchema,
  password: passwordInput.optional(),
});
export type CreateAdminInput = z.infer<typeof createAdminInputSchema>;

/** `PATCH /admin/admins/:id`. Modification partielle. */
export const updateAdminInputSchema = z.object({
  prenom: z.string().trim().min(2, 'Indiquez le prénom.').optional(),
  nom: z.string().trim().min(2, 'Indiquez le nom.').optional(),
  email: emailInput.optional(),
  role: adminRoleSchema.optional(),
  actif: z.boolean().optional(),
  password: passwordInput.optional(),
});
export type UpdateAdminInput = z.infer<typeof updateAdminInputSchema>;

export const registerWithGoogleInputSchema = z.object({
  ticket: z.string().min(1),
  prenom: z.string().trim().min(2, 'Indiquez votre prénom.'),
  nom: z.string().trim().min(2, 'Indiquez votre nom.'),
  indicatif: z.string().min(2),
  telephone: phoneInput,
  fonction: fonctionSchema,
  exercice: exerciceSchema,
  cguAccepted: z.literal(true, { message: 'Vous devez accepter les conditions.' }),
});
export type RegisterWithGoogleInput = z.infer<typeof registerWithGoogleInputSchema>;

export const registerInputSchema = z.object({
  prenom: z.string().trim().min(2, 'Indiquez votre prénom.'),
  nom: z.string().trim().min(2, 'Indiquez votre nom.'),
  email: emailInput,
  indicatif: z.string().min(2),
  telephone: phoneInput,
  fonction: fonctionSchema,
  exercice: exerciceSchema,
  cguAccepted: z.literal(true, { message: 'Vous devez accepter les conditions.' }),
});
export type RegisterInput = z.infer<typeof registerInputSchema>;

/** `POST /users` : le compte créé est toujours un `membre`. */
export const createUserInputSchema = z.object({
  prenom: z.string().trim().min(2, 'Indiquez le prénom.'),
  nom: z.string().trim().min(2, 'Indiquez le nom.'),
  email: emailInput,
  indicatif: z.string().min(2),
  telephone: phoneInput,
  fonction: fonctionSchema,
});
export type CreateUserInput = z.infer<typeof createUserInputSchema>;

/**
 * `PATCH /users/:id` : modification partielle, seuls les champs présents sont
 * appliqués. Sert aussi bien à corriger une identité qu'à promouvoir ou
 * désactiver un accès.
 */
export const updateUserInputSchema = z.object({
  prenom: z.string().trim().min(2, 'Indiquez le prénom.').optional(),
  nom: z.string().trim().min(2, 'Indiquez le nom.').optional(),
  email: emailInput.optional(),
  indicatif: z.string().min(2).optional(),
  telephone: phoneInput.optional(),
  fonction: fonctionSchema.optional(),
  role: roleSchema.optional(),
  actif: z.boolean().optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserInputSchema>;

export const onboardingInputSchema = z.object({
  nomCabinet: z.string().trim().min(2, 'Nom du cabinet requis'),
  effectif: effectifSchema.optional(),
});
export type OnboardingInput = z.infer<typeof onboardingInputSchema>;

/** `POST /billing/checkout` : formule et période choisies. */
export const checkoutInputSchema = z.object({
  formule: formuleSchema,
  periode: periodeSchema,
});
export type CheckoutInput = z.infer<typeof checkoutInputSchema>;

/** Réponse commune aux sessions Stripe (Checkout, portail) : une URL à suivre. */
export const stripeSessionSchema = z.object({ url: z.url() });
export type StripeSession = z.infer<typeof stripeSessionSchema>;

/**
 * `POST /billing/checkout` : première souscription (caisse Stripe), montée en
 * gamme appliquée tout de suite, ou rétrogradation datée au renouvellement.
 */
export const checkoutResultSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('caisse'), url: z.url() }),
  z.object({
    action: z.literal('change_immediat'),
    formule: formuleSchema,
    periode: periodeSchema,
  }),
  z.object({
    action: z.literal('change_programme'),
    formule: formuleSchema,
    periode: periodeSchema,
    dateEffet: z.iso.datetime(),
  }),
]);
export type CheckoutResult = z.infer<typeof checkoutResultSchema>;

/** `GET /billing/credit` : avoir Stripe restant, en euros. */
export const billingCreditSchema = z.object({ montant: z.number() });
export type BillingCredit = z.infer<typeof billingCreditSchema>;

/**
 * `GET /billing/annulation/apercu` et `POST /billing/annulation` : date de
 * fin d'accès (un mois de préavis, au plus tard le renouvellement) et avoir
 * rendu sur la période déjà payée non consommée.
 */
export const cancelSubscriptionResultSchema = z.object({
  dateFin: z.iso.datetime(),
  montantCredit: z.number(),
});
export type CancelSubscriptionResult = z.infer<typeof cancelSubscriptionResultSchema>;

export const paiementSchema = z.object({
  id: z.string(),
  date: z.iso.datetime(),
  montant: z.number(),
  /** `offert` : facture à 0 €, rien n'a été encaissé ni dû. */
  statut: z.enum(['paye', 'offert', 'echec']),
  /** Formule facturée. `null` si le Price n'est pas reconnu (tarif archivé). */
  formule: formuleSchema.nullable(),
  /** Cadence facturée. `null`, même cause que `formule`. */
  periode: periodeSchema.nullable(),
  /** Fin de la période couverte par ce paiement. `null`, même cause que `periode`. */
  expireLe: z.iso.datetime().nullable(),
  factureUrl: z.url().nullable(),
  carte: z.object({ marque: z.string(), derniers4: z.string() }).nullable(),
});
export type Paiement = z.infer<typeof paiementSchema>;

export const paiementListSchema = z.object({ items: z.array(paiementSchema) });
export type PaiementList = z.infer<typeof paiementListSchema>;

export const transactionStatutSchema = z.enum([
  'paye',
  /** Entièrement effacée par une remise : rien n'a été encaissé. */
  'offerte',
  'ouverte',
  'impayee',
  'annulee',
  'brouillon',
]);
export type TransactionStatut = z.infer<typeof transactionStatutSchema>;

export const transactionTypeSchema = z.enum([
  'souscription',
  'renouvellement',
  'changement',
  'avoir',
  'manuelle',
  'autre',
]);
export type TransactionType = z.infer<typeof transactionTypeSchema>;

/**
 * `GET /admin/billing/transactions` : une facture Stripe par ligne, rapprochée
 * du cabinet qui l'a payée. `cabinet` est `null` quand le client Stripe n'a
 * plus de cabinet en base — la ligne reste affichée pour ne pas escamoter de
 * l'argent encaissé.
 */
export const transactionSchema = z.object({
  id: z.string(),
  numero: z.string().nullable(),
  date: z.iso.datetime(),
  montant: z.number(),
  statut: transactionStatutSchema,
  type: transactionTypeSchema,
  avoirCree: z.number(),
  avoirApplique: z.number(),
  remise: z.number(),
  formule: formuleSchema.nullable(),
  periode: periodeSchema.nullable(),
  factureUrl: z.url().nullable(),
  stripeCustomerId: z.string(),
  stripeUrl: z.url().nullable(),
  cabinet: z
    .object({
      id: z.string(),
      nom: z.string(),
      responsable: z.string(),
      email: z.string(),
    })
    .nullable(),
});
export type Transaction = z.infer<typeof transactionSchema>;

export const adminTransactionListSchema = z.object({
  items: z.array(transactionSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  encaisse: z.number(),
  tronque: z.boolean(),
  /** Renseignés seulement quand la requête cible un cabinet. */
  credit: z.number().nullable(),
  clientUrl: z.url().nullable(),
});
export type AdminTransactionList = z.infer<typeof adminTransactionListSchema>;

/* ------------------------------------------------------------------ */
/* Messages                                                            */
/* ------------------------------------------------------------------ */

/**
 * Avant ou après la consultation. Il n'y a pas de troisième moment.
 *
 * Les champs des messages sont nommés en anglais, comme la base : ils viennent
 * d'une partie du produit écrite après coup. Seuls les jetons de variables
 * (`{{prenom}}`) restent en français — c'est du contenu, écrit dans des textes
 * que les cabinets ont déjà retouchés.
 */
export const messageTimingSchema = z.enum(['before', 'after']);
export type MessageTiming = z.infer<typeof messageTimingSchema>;

export const messageTemplateSchema = z.object({
  id: z.string(),
  /** Catégorie du catalogue : `google-review`, `appointment-reminder`… */
  catalogKey: z.string(),
  /**
   * Filet de sécurité de la catégorie : il part quand aucun cas ciblé n'a pris
   * le rendez-vous, ne vise jamais de motif, et ne se supprime pas.
   */
  isFallback: z.boolean(),
  /**
   * Rang du cas dans sa catégorie. Règle d'envoi et non classement : le
   * premier dont les motifs correspondent l'emporte, le filet passe dernier.
   */
  position: z.number().int(),
  name: z.string(),
  subject: z.string(),
  body: z.string(),
  enabled: z.boolean(),
  timing: messageTimingSchema,
  /** Écart avec l'heure du rendez-vous, en heures, toujours positif. */
  offsetHours: z.number().int(),
  /** Heure fixe d'envoi (0-23), `null` pour l'heure exacte du décalage. */
  sendHour: z.number().int().nullable(),
  /** Identifiants Doctolib visés. **Vide vaut « tous »**, jamais « aucun ». */
  agendaIds: z.array(z.number().int()),
  motifIds: z.array(z.number().int()),
  updatedAt: z.iso.datetime(),
});
export type MessageTemplate = z.infer<typeof messageTemplateSchema>;

/** Un soin qu'une catégorie de consignes propose à sa mise en route. */
export const careOptionSchema = z.object({
  key: z.string(),
  title: z.string(),
  detail: z.string(),
  /** Mots servant à retrouver le motif dans le relevé Doctolib. */
  keywords: z.array(z.string()),
  subject: z.string(),
  body: z.string(),
});
export type CareOption = z.infer<typeof careOptionSchema>;

/**
 * Une catégorie du catalogue, telle que le serveur la livre.
 *
 * Le catalogue n'est pas déclaré ici : il appartient au backend, qui décide
 * quel texte part. L'app l'affiche, et `defaults` ne sert qu'à montrer le
 * texte d'origine d'une catégorie encore jamais retouchée.
 */
export const messageCatalogEntrySchema = z.object({
  key: z.string(),
  description: z.string(),
  /** Catégorie sans réglages : un seul message, pour tout le cabinet. */
  locked: z.boolean(),
  /** Catégorie qui se règle, mais n'accepte qu'un message. */
  singleCase: z.boolean(),
  defaults: z.object({
    name: z.string(),
    subject: z.string(),
    body: z.string(),
    timing: messageTimingSchema,
    offsetHours: z.number().int(),
    sendHour: z.number().int().nullable(),
  }),
  careOptions: z.array(careOptionSchema),
});
export type MessageCatalogEntry = z.infer<typeof messageCatalogEntrySchema>;

/** `GET /messages`. */
export const messagesResultSchema = z.object({
  templates: z.array(messageTemplateSchema),
  catalog: z.array(messageCatalogEntrySchema),
  /** `false` tant qu'aucune adresse d'expédition n'est déclarée. */
  smtpConfigured: z.boolean(),
  /**
   * Envois restants sur le forfait offert par le relais Fidalia, `null` quand
   * le cabinet a son propre serveur — le forfait n'a alors plus d'objet.
   *
   * Tant qu'il en reste, les messages partent : l'écran ne réclame donc rien.
   * À zéro, l'adresse du cabinet devient nécessaire, et c'est là qu'on le dit.
   */
  platformSendsLeft: z.number().int().nullable(),
  /** Taille du forfait, pour dire « 12 sur 100 ». */
  platformFreeSends: z.number().int(),
});
export type MessagesResult = z.infer<typeof messagesResultSchema>;

export const messageResultSchema = z.object({ template: messageTemplateSchema });
export type MessageResult = z.infer<typeof messageResultSchema>;

export const messageListResultSchema = z.object({ templates: z.array(messageTemplateSchema) });
export type MessageListResult = z.infer<typeof messageListResultSchema>;

/** Corps de `POST /messages` : un cas ajouté à une catégorie. */
export type CreateMessageInput = {
  catalogKey: string;
  name?: string;
  subject?: string;
  body?: string;
  timing?: MessageTiming;
  offsetHours?: number;
  sendHour?: number | null;
  agendaIds?: number[];
  motifIds?: number[];
};

/**
 * Corps de `PATCH /messages/:id`. `enabled` n'en fait pas partie : allumer
 * vérifie des prérequis côté serveur, et passe par `useSetMessageEnabled`.
 */
export type UpdateMessageInput = {
  name?: string;
  subject?: string;
  body?: string;
  timing?: MessageTiming;
  offsetHours?: number;
  sendHour?: number | null;
  agendaIds?: number[];
  motifIds?: number[];
};

/**
 * Corps de `PUT /messages/:id/enabled`. `googleReviewUrl` et `sendHour` sont
 * ce que la mise en route apporte, selon la catégorie : ils voyagent avec
 * l'allumage pour que les deux tombent ensemble ou pas du tout.
 */
export type SetMessageEnabledInput = {
  enabled: boolean;
  googleReviewUrl?: string;
  sendHour?: number | null;
};

export const messageTestResultSchema = z.object({ deliveryId: z.string() });
export type MessageTestResult = z.infer<typeof messageTestResultSchema>;

export const googleReviewUrlResultSchema = z.object({ googleReviewUrl: z.url() });
export type GoogleReviewUrlResult = z.infer<typeof googleReviewUrlResultSchema>;

/* ------------------------------------------------------------------ */
/* Réglages d'envoi                                                    */
/* ------------------------------------------------------------------ */

/**
 * Chiffrement du lien avec le serveur. `none` existe pour les relais internes
 * d'un cabinet, mais ce n'est jamais le bon choix sur Internet.
 */
export const smtpEncryptionSchema = z.enum(['starttls', 'ssl', 'none']);
export type SmtpEncryption = z.infer<typeof smtpEncryptionSchema>;

/**
 * Le compte d'envoi tel que l'API le rend : **sans le mot de passe**. Un
 * secret enregistré ne se relit pas, le formulaire repart avec un champ vide
 * et ne remplace la clé que s'il est rempli.
 */
export const smtpAccountSchema = z.object({
  senderName: z.string(),
  email: z.string(),
  host: z.string(),
  port: z.number().int(),
  encryption: smtpEncryptionSchema,
  /** `null` = jamais testée, ce qui n'est pas une panne. */
  lastTestedAt: z.iso.datetime().nullable(),
  lastTestError: z.string().nullable(),
});
export type SmtpAccount = z.infer<typeof smtpAccountSchema>;

export const emailSettingsSchema = z.object({
  smtp: smtpAccountSchema.nullable(),
  signature: z.object({
    /**
     * Jamais vide : tant que le cabinet n'a rien écrit, c'est la signature par
     * défaut — son nom, son téléphone, son adresse.
     */
    body: z.string(),
    withLogo: z.boolean(),
    /**
     * `true` quand le texte est celui calculé d'après les coordonnées du
     * cabinet, et non un texte qu'il a enregistré.
     */
    isDefault: z.boolean(),
  }),
  googleReviewUrl: z.string().nullable(),
});
export type EmailSettings = z.infer<typeof emailSettingsSchema>;

export const emailSettingsResultSchema = z.object({ settings: emailSettingsSchema });
export type EmailSettingsResult = z.infer<typeof emailSettingsResultSchema>;

/**
 * `PUT /email-settings/smtp`. `testError` porte le refus du serveur d'envoi
 * quand il y en a un : le compte est enregistré d'abord, testé ensuite, pour
 * qu'un refus ne fasse pas perdre la saisie.
 */
export const smtpSaveResultSchema = z.object({
  smtp: smtpAccountSchema,
  testError: z.string().nullable(),
});
export type SmtpSaveResult = z.infer<typeof smtpSaveResultSchema>;

export const smtpTestResultSchema = z.object({ smtp: smtpAccountSchema });
export type SmtpTestResult = z.infer<typeof smtpTestResultSchema>;

/** Corps de `PUT /email-settings/smtp`. Mot de passe vide = inchangé. */
export type SaveSmtpInput = {
  senderName: string;
  email: string;
  host: string;
  port: number;
  encryption: SmtpEncryption;
  password?: string;
};

export const signatureResultSchema = z.object({
  signature: z.object({ body: z.string(), withLogo: z.boolean(), isDefault: z.boolean() }),
});
export type SignatureResult = z.infer<typeof signatureResultSchema>;

/* ------------------------------------------------------------------ */
/* Back-office : suivi des envois                                      */
/* ------------------------------------------------------------------ */

/** Les cabinets que le support veut isoler dans la liste. */
export const emailActivityFilterSchema = z.enum(['all', 'at-cap', 'failing', 'no-smtp']);
export type EmailActivityFilter = z.infer<typeof emailActivityFilterSchema>;

/**
 * Une ligne de suivi par cabinet.
 *
 * Trois raisons possibles à un cabinet qui n'envoie plus — pas d'adresse
 * déclarée, plafond atteint, serveur qui refuse — et la ligne les distingue
 * sans qu'on ait à ouvrir quoi que ce soit.
 */
export const emailActivityRowSchema = z.object({
  cabinetId: z.string(),
  cabinet: z.string(),
  smtpConfigured: z.boolean(),
  smtpEmail: z.string().nullable(),
  smtpHost: z.string().nullable(),
  /** Dernier refus de connexion enregistré par un test. */
  smtpTestError: z.string().nullable(),
  /** Plafond appliqué, et le réglage propre au cabinet s'il en a un. */
  hourlyLimit: z.number().int(),
  hourlyLimitOverride: z.number().int().nullable(),
  usedLastHour: z.number().int(),
  atCap: z.boolean(),
  sent24h: z.number().int(),
  failed24h: z.number().int(),
  /** Envois automatiques en attente de reprise (refus temporaire). */
  pending24h: z.number().int(),
  /**
   * Envois portés par le relais Fidalia depuis l'inscription, et ce qu'il reste
   * du forfait (`null` dès que le cabinet a son propre serveur). C'est notre
   * exposition : ces messages sont partis de `fidalia.fr`.
   */
  platformSent: z.number().int(),
  platformSendsLeft: z.number().int().nullable(),
  lastError: z.string().nullable(),
  lastErrorAt: z.iso.datetime().nullable(),
  lastSentAt: z.iso.datetime().nullable(),
});
export type EmailActivityRow = z.infer<typeof emailActivityRowSchema>;

export const emailActivitySummarySchema = z.object({
  /** Plafond appliqué aux cabinets sans réglage propre. */
  platformHourlyLimit: z.number().int(),
  /** Forfait de relais offert à chaque cabinet. */
  platformFreeSends: z.number().int(),
  /** Ce que notre domaine a porté sur 24 h, pour les cabinets sans adresse. */
  relaySent24h: z.number().int(),
  /** Cabinets qui envoient encore par le relais. */
  cabinetsOnRelay: z.number().int(),
  sentLastHour: z.number().int(),
  sent24h: z.number().int(),
  failed24h: z.number().int(),
  pending24h: z.number().int(),
  cabinetsAtCap: z.number().int(),
  cabinetsWithoutSmtp: z.number().int(),
  cabinetsActifs: z.number().int(),
});
export type EmailActivitySummary = z.infer<typeof emailActivitySummarySchema>;

export const emailActivityListSchema = z.object({
  summary: emailActivitySummarySchema,
  items: z.array(emailActivityRowSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
});
export type EmailActivityList = z.infer<typeof emailActivityListSchema>;

export const messageDeliveryStatusSchema = z.enum([
  'pending',
  'scheduled',
  'blocked',
  'sending',
  'sent',
  'failed',
  'cancelled',
  'skipped',
]);
export type MessageDeliveryStatus = z.infer<typeof messageDeliveryStatusSchema>;

export const messageDeliveryKindSchema = z.enum(['test', 'scheduled']);
export type MessageDeliveryKind = z.infer<typeof messageDeliveryKindSchema>;

/**
 * Un envoi, vu du back-office : **métadonnées seules**.
 *
 * Ni objet ni corps, et le domaine du destinataire à la place de son adresse :
 * un envoi porte le nom du patient et la nature de son soin, qu'un
 * administrateur plateforme n'a pas à lire pour diagnostiquer un quota.
 */
export const messageSenderSchema = z.enum(['cabinet', 'platform']);
export type MessageSender = z.infer<typeof messageSenderSchema>;

export const adminDeliverySchema = z.object({
  id: z.string(),
  cabinet: z.object({ id: z.string(), nom: z.string(), userId: z.string().nullable() }),
  kind: messageDeliveryKindSchema,
  /** Serveur qui a porté l'envoi : celui du cabinet, ou le relais Fidalia. */
  sender: messageSenderSchema,
  status: messageDeliveryStatusSchema,
  /** Adresse d'expédition actuelle du cabinet, ou celle du relais Fidalia. */
  senderEmail: z.string().nullable(),
  recipientEmail: z.string(),
  catalogKey: z.string().nullable(),
  /** Ce que le serveur a répondu, en clair. */
  error: z.string().nullable(),
  /** Code SMTP numérique (`421`, `535`, `550`…), quand il y en a un. */
  responseCode: z.number().int().nullable(),
  /** Code nodemailer (`EAUTH`, `ETIMEDOUT`…), à défaut de code SMTP. */
  failureCode: z.string().nullable(),
  attempts: z.number().int(),
  sentAt: z.iso.datetime().nullable(),
  /** Heure d'envoi prévue d'un message programmé. */
  scheduledFor: z.iso.datetime().nullable().default(null),
  createdAt: z.iso.datetime(),
});
export type AdminDelivery = z.infer<typeof adminDeliverySchema>;

export const adminDeliveryListSchema = z.object({
  items: z.array(adminDeliverySchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  /** Compteurs de la sélection courante, hors pagination et hors filtre de statut. */
  counts: z.object({
    sent: z.number().int(),
    /** Programmés, en cours d'envoi. */
    scheduled: z.number().int(),
    blocked: z.number().int(),
    failed: z.number().int(),
    /** Annulés, ignorés. */
    cancelled: z.number().int(),
  }),
});
/** Les onglets de la liste des envois ; chacun regroupe un ou deux statuts. */
export type AdminDeliveryGroup = keyof z.infer<typeof adminDeliveryListSchema>['counts'];
export type AdminDeliveryList = z.infer<typeof adminDeliveryListSchema>;

export const cabinetEmailLimitSchema = z.object({
  cabinetId: z.string(),
  hourlyLimitOverride: z.number().int().nullable(),
  hourlyLimit: z.number().int(),
});
export type CabinetEmailLimit = z.infer<typeof cabinetEmailLimitSchema>;

/* ------------------------------------------------------------------ */
/* Identité du cabinet                                                 */
/* ------------------------------------------------------------------ */

/**
 * D'où vient une valeur de l'identité du cabinet.
 *
 * `cabinet` : saisie dans les réglages. `doctolib` : reprise du dernier
 * relevé. `user` : le téléphone du compte responsable, faute de mieux.
 * `null` : personne ne la connaît.
 */
export const cabinetIdentitySourceSchema = z.enum(['cabinet', 'doctolib', 'user']).nullable();
export type CabinetIdentitySource = z.infer<typeof cabinetIdentitySourceSchema>;

/**
 * `GET /cabinets/profile`.
 *
 * `effective` est ce qui s'applique — ce que le patient lira. `saisi` est ce
 * que le cabinet a tapé, vide tant qu'il n'a rien rempli : le formulaire
 * affiche le premier et enregistre le second, et `sources` dit lesquels sont
 * repris de Doctolib.
 */
export const cabinetProfileSchema = z.object({
  /** Raison sociale de l'inscription : factures et back-office. Non modifiable. */
  legalName: z.string(),
  effective: z.object({
    nom: z.string(),
    telephone: z.string().nullable(),
    adresse: z.string().nullable(),
    codePostal: z.string().nullable(),
    ville: z.string().nullable(),
  }),
  saisi: z.object({
    displayName: z.string().nullable(),
    telephone: z.string().nullable(),
    adresse: z.string().nullable(),
    codePostal: z.string().nullable(),
    ville: z.string().nullable(),
  }),
  sources: z.object({
    nom: cabinetIdentitySourceSchema,
    telephone: cabinetIdentitySourceSchema,
    adresse: cabinetIdentitySourceSchema,
  }),
  /** Adresse publique du logo, `null` s'il n'y en a pas. */
  logoUrl: z.url().nullable(),
});
export type CabinetProfile = z.infer<typeof cabinetProfileSchema>;

export const cabinetProfileResultSchema = z.object({ profile: cabinetProfileSchema });
export type CabinetProfileResult = z.infer<typeof cabinetProfileResultSchema>;

/**
 * Corps de `PATCH /cabinets/profile`. Seuls les champs présents sont écrits ;
 * une chaîne vide rend le champ à ce que Doctolib rapporte.
 */
export type UpdateCabinetProfileInput = {
  displayName?: string;
  telephone?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
};

/* ------------------------------------------------------------------ */
/* File d'attente                                                      */
/* ------------------------------------------------------------------ */

export const deliveryViewSchema = z.enum(['a-envoyer', 'envoyes', 'echecs']);
export type DeliveryView = z.infer<typeof deliveryViewSchema>;

/** L'état d'une ligne. `sending` : réclamée par le worker, plus annulable. */
export const deliveryStateSchema = z.enum([
  'scheduled',
  'sending',
  'blocked',
  'sent',
  'failed',
  'cancelled',
  'skipped',
]);
export type DeliveryState = z.infer<typeof deliveryStateSchema>;

/**
 * Une ligne de la file. `reasonCode` est stable (le front peut s'y fier),
 * `reason` est le texte à afficher. `canEdit`, `canCancel` et `canResend`
 * viennent du serveur : l'app n'a rien à deviner, et le serveur les refait
 * valoir à l'écriture.
 */
export const deliverySchema = z.object({
  id: z.string(),
  state: deliveryStateSchema,
  patient: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().nullable(),
  }),
  catalogKey: z.string(),
  templateName: z.string(),
  practitioner: z.string(),
  agenda: z.string(),
  motif: z.string(),
  appointmentAt: z.iso.datetime().nullable(),
  sendAt: z.iso.datetime(),
  reasonCode: z.string().nullable(),
  reason: z.string().nullable(),
  canEdit: z.boolean(),
  canCancel: z.boolean(),
  /** Un message « après » encore programmé peut partir tout de suite. */
  canSendNow: z.boolean().default(false),
  /** Un envoi annulé peut être remis en file. */
  canReschedule: z.boolean().default(false),
  canResend: z.boolean(),
  /** Le patient a ouvert le formulaire d'avis depuis ce mail. */
  formOpenedAt: z.iso.datetime().nullable().default(null),
  /** « Coup de cœur » : `null` si l'option est coupée ou si ce n'est pas un message d'avis Google. */
  heart: z.boolean().nullable().default(null),
  version: z.number().int(),
});
export type Delivery = z.infer<typeof deliverySchema>;

export const deliveryListSchema = z.object({
  items: z.array(deliverySchema),
  nextCursor: z.string().nullable(),
});
export type DeliveryList = z.infer<typeof deliveryListSchema>;

export const deliveryCountsSchema = z.object({
  toSend: z.number().int().nonnegative(),
  sent: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type DeliveryCounts = z.infer<typeof deliveryCountsSchema>;

export const deliveryDetailSchema = deliverySchema.extend({
  /** Le message tel que le patient le lira, `null` si le rendez-vous a été purgé. */
  preview: z
    .object({ subject: z.string(), text: z.string(), html: z.string().nullish() })
    .nullable(),
  /** Le texte source, variables comprises : de quoi pré-remplir une retouche. */
  source: z.object({ subject: z.string(), body: z.string(), edited: z.boolean() }),
  events: z.array(z.object({ type: z.string(), at: z.iso.datetime(), payload: z.unknown() })),
});
export type DeliveryDetail = z.infer<typeof deliveryDetailSchema>;

export const deliveryIdSchema = z.object({ id: z.string() });

export type DeliveryFilters = {
  view: DeliveryView;
  /** `yyyy-mm-dd`, bornes incluses. */
  from?: string;
  to?: string;
  types?: readonly string[];
  /** Identifiants d'agendas Doctolib ; vide ou absent = tous les agendas. */
  agendas?: readonly number[];
  q?: string;
};

export type UpdateDeliveryInput = { subject: string; body: string; version: number };

/** `GET /admin/emails/queue` : l'état de la file d'attente, pour l'exploitation. */
export const queueHealthSchema = z.object({
  ok: z.boolean(),
  workers: z.array(z.object({ id: z.string(), beatAt: z.iso.datetime(), stale: z.boolean() })),
  byStatus: z.record(z.string(), z.number().int()),
  overdue: z.object({ count: z.number().int(), oldestMinutes: z.number().nullable() }),
  failuresLast24h: z.array(z.object({ reasonCode: z.string(), count: z.number().int() })),
  pausedCabinets: z.array(
    z.object({
      id: z.string(),
      nom: z.string(),
      reason: z.string().nullable(),
      since: z.iso.datetime(),
    }),
  ),
});
export type QueueHealth = z.infer<typeof queueHealthSchema>;

/**
 * `GET /doctolib/appointments/context` : le fuseau du cabinet, les agendas de
 * praticien à relever, et la dernière fois que l'extension a lu ses rendez-vous.
 */
export const appointmentsContextSchema = z.object({
  timeZone: z.string(),
  /** `null` : le cabinet n'a jamais synchronisé ses agendas. */
  practitionerAgendaIds: z.array(z.number().int()).nullable(),
  lastSyncAt: z.iso.datetime().nullable(),
});
export type AppointmentsContext = z.infer<typeof appointmentsContextSchema>;

/* ------------------------------------------------------------------ */
/* Avis                                                                */
/* ------------------------------------------------------------------ */

export const reviewCriterionSchema = z.object({
  id: z.string(),
  label: z.string(),
  type: z.enum(['stars', 'text']),
});
export type ReviewCriterion = z.infer<typeof reviewCriterionSchema>;

export const reviewStatusSchema = z.enum(['to_review', 'seen', 'done']);
export type ReviewStatus = z.infer<typeof reviewStatusSchema>;

export const reviewSchema = z.object({
  id: z.string(),
  patient: z.object({ name: z.string(), email: z.string() }),
  createdAt: z.iso.datetime(),
  status: reviewStatusSchema,
  redirected: z.boolean(),
  /** Quand le patient a cliqué sur « Laisser un avis Google » ; `null` sinon. */
  googleClickedAt: z.iso.datetime().nullable(),
  /** Agenda (praticien) du rendez-vous d'où vient l'avis ; `null` sur le lien public. */
  agenda: z.string().nullable(),
  message: z.string(),
  questions: z.array(reviewCriterionSchema),
  /** Identifiant de critère -> étoiles ou texte ; `global` porte la note globale. */
  answers: z.record(z.string(), z.union([z.number(), z.string()])),
});
export type Review = z.infer<typeof reviewSchema>;

/** `GET /admin/reviews` : avis de tous les cabinets, lecture seule. */
export const adminReviewSchema = z.object({
  id: z.string(),
  cabinetId: z.string(),
  cabinetNom: z.string(),
  /** E-mail du compte principal du cabinet. */
  cabinetEmail: z.string().nullable(),
  /** Compte principal : la fiche cabinet de la console s'ouvre par son identifiant. */
  cabinetUserId: z.string().nullable(),
  patientName: z.string(),
  /** Vide pour un patient anonyme (lien public) : seuls les patients suivis ont une adresse. */
  patientEmail: z.string(),
  createdAt: z.iso.datetime(),
  redirected: z.boolean(),
  /** Quand le patient a cliqué sur « Laisser un avis sur Google » ; `null` sinon. */
  googleClickedAt: z.iso.datetime().nullable(),
  /** Agenda (praticien) du rendez-vous d'où vient l'avis ; `null` sur le lien public. */
  agenda: z.string().nullable(),
  message: z.string(),
  questions: z.array(reviewCriterionSchema),
  answers: z.record(z.string(), z.union([z.number(), z.string()])),
});
export type AdminReview = z.infer<typeof adminReviewSchema>;

export const adminReviewListSchema = z.object({
  items: z.array(adminReviewSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  /** Moyenne de la sélection, `null` sans avis. */
  average: z.number().nullable(),
  /** Tous les cabinets ayant des avis, indépendamment du filtre. */
  cabinets: z.array(z.object({ id: z.string(), nom: z.string(), count: z.number().int() })),
});
export type AdminReviewList = z.infer<typeof adminReviewListSchema>;

/** `GET /admin/reviews/forms` : le formulaire public de chaque cabinet. */
export const adminReviewFormSchema = z.object({
  cabinetId: z.string(),
  cabinetNom: z.string(),
  cabinetEmail: z.string().nullable(),
  cabinetUserId: z.string().nullable(),
  /** Lien public signé du formulaire. */
  url: z.string(),
  questions: z.number().int(),
  noteEnabled: z.boolean(),
  /** Ouvertures du formulaire depuis le bouton du mail. */
  formViews: z.number().int(),
  /** Lien de la fiche Google du cabinet, `null` tant qu'il n'est pas renseigné. */
  googleReviewUrl: z.string().nullable(),
  reviews: z.number().int(),
  /** Note moyenne du cabinet, `null` sans avis. */
  average: z.number().nullable(),
});
export type AdminReviewForm = z.infer<typeof adminReviewFormSchema>;

export const adminReviewFormListSchema = z.object({
  items: z.array(adminReviewFormSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
});
export type AdminReviewFormList = z.infer<typeof adminReviewFormListSchema>;

/** `GET /admin/messages` : modèles de messages de tous les cabinets, lecture seule. */
export const adminMessageSchema = z.object({
  id: z.string(),
  cabinetId: z.string(),
  cabinetNom: z.string(),
  cabinetEmail: z.string().nullable(),
  cabinetUserId: z.string().nullable(),
  /** Catégorie du catalogue : `google-review`, `appointment-reminder`… */
  catalogKey: z.string(),
  /** Message par défaut de la catégorie, par opposition à un cas ciblé sur des motifs. */
  fallback: z.boolean(),
  name: z.string(),
  subject: z.string(),
  enabled: z.boolean(),
  timing: z.enum(['before', 'after']),
  offsetHours: z.number().int(),
  sendHour: z.number().int().nullable(),
  /** Motifs visés ; 0 vaut « tous ». */
  motifs: z.number().int(),
  /** Les mêmes, nommés, pour l'infobulle. */
  motifsListe: z.array(z.object({ nom: z.string(), couleur: z.string().nullable() })),
  sent: z.number().int(),
  updatedAt: z.iso.datetime(),
});
export type AdminMessage = z.infer<typeof adminMessageSchema>;

export const adminMessageListSchema = z.object({
  items: z.array(adminMessageSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
});
export type AdminMessageList = z.infer<typeof adminMessageListSchema>;

/** `GET /admin/messages/:id/preview` : de quoi dessiner un modèle tel que le patient le reçoit. */
export const adminMessagePreviewSchema = z.object({
  name: z.string(),
  subject: z.string(),
  body: z.string(),
  /** Enseigne du cabinet : la valeur de `{{cabinet}}`. */
  cabinet: z.string(),
  sender: z.object({ nom: z.string(), email: z.string() }),
  logoUrl: z.string().nullable(),
  signature: z.object({ body: z.string(), withLogo: z.boolean() }),
  hasReviewButton: z.boolean(),
  /** Envois réussis de ce modèle. */
  sent: z.number().int(),
  /** Motifs Doctolib visés ; vide = tous. */
  motifs: z.array(z.object({ nom: z.string(), couleur: z.string().nullable() })),
});
export type AdminMessagePreview = z.infer<typeof adminMessagePreviewSchema>;

/** `GET /admin/extension/syncs` : journal des synchronisations reçues de l'extension. */
export const extensionSyncSchema = z.object({
  id: z.string(),
  kind: z.enum(['doctolib', 'appointments']),
  ok: z.boolean(),
  error: z.string().nullable(),
  /** Compteurs de la synchronisation (agendas, motifs, reçus, créés…). */
  stats: z.record(z.string(), z.number()),
  createdAt: z.iso.datetime(),
  cabinetId: z.string(),
  cabinetNom: z.string(),
  cabinetUserId: z.string().nullable(),
  member: z.object({ nom: z.string(), email: z.string() }).nullable(),
});
export type ExtensionSync = z.infer<typeof extensionSyncSchema>;

export const extensionSyncListSchema = z.object({
  items: z.array(extensionSyncSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
});
export type ExtensionSyncList = z.infer<typeof extensionSyncListSchema>;

/** `GET /reviews`. */
export const reviewListSchema = z.object({
  /** Patients distincts à chaque étape d'une demande d'avis par mail (un patient revu compte une fois). */
  patients: z.object({
    /** Ont reçu la demande. */
    sent: z.number().int(),
    /** Ont ouvert le formulaire depuis le bouton du mail. */
    opened: z.number().int(),
    /** Ont laissé un avis. */
    reviewed: z.number().int(),
    /** Satisfaits (note au-dessus du seuil) : on leur a proposé la fiche Google. */
    redirected: z.number().int(),
  }),
  /** Avis (et non patients) laissés depuis un mail. */
  formReviews: z.number().int(),
  /** Avis laissés sur le lien public du formulaire, sans passer par un mail. */
  formDirect: z.number().int(),
  /** Fiche Google du cabinet, `null` tant qu'elle n'est pas renseignée. */
  googleReviewUrl: z.string().nullable(),
  /** Noms des agendas de praticien du cabinet, même sans avis. */
  agendas: z.array(z.string()),
  reviews: z.array(reviewSchema),
});
export type ReviewList = z.infer<typeof reviewListSchema>;

/** `GET` et `PUT /reviews/config` : seules les questions supplémentaires, la note globale est implicite. */
export const reviewConfigSchema = z.object({
  criteria: z.array(reviewCriterionSchema),
  noteEnabled: z.boolean(),
  logoEnabled: z.boolean(),
  /** Note minimale (1 à 5) pour envoyer le patient sur Google. */
  googleThreshold: z.number().int().min(1).max(5),
  /** Option « Coup de cœur » de la notification « Vu ». */
  heartEnabled: z.boolean(),
  /** Délai entre le « Vu » et l'envoi de la demande d'avis, en minutes. */
  sendDelayMinutes: z.number().int().default(60),
  publicUrl: z.string(),
});
export type ReviewConfig = z.infer<typeof reviewConfigSchema>;
export type SaveReviewConfigInput = Omit<
  ReviewConfig,
  'publicUrl' | 'googleThreshold' | 'heartEnabled' | 'sendDelayMinutes'
>;
export type SaveGoogleSettingsInput = Pick<ReviewConfig, 'googleThreshold' | 'heartEnabled'> & {
  /** Absent : le délai d'envoi du cabinet ne change pas. */
  sendDelayMinutes?: number;
};

/** `GET /reviews/public/:token`, sans authentification. */
export const publicReviewFormSchema = z.object({
  cabinetName: z.string(),
  logoUrl: z.string().nullable(),
  criteria: z.array(reviewCriterionSchema),
  noteEnabled: z.boolean(),
  patientFirstName: z.string().nullable(),
  /** « Coup de cœur » : la fiche Google où envoyer ce patient sans formulaire. */
  directUrl: z.string().nullable().default(null),
});
export type PublicReviewForm = z.infer<typeof publicReviewFormSchema>;

export type SubmitReviewInput = {
  rating: number;
  message: string;
  answers: Record<string, number | string>;
  name?: string;
};

/** `POST /reviews/public/:token` : où envoyer le patient (la fiche Google), s'il y a lieu. */
export const submitReviewResultSchema = z.object({
  redirectUrl: z.string().nullable(),
  /** Pour suivre le clic sur « Laisser un avis Google ». */
  reviewId: z.string(),
});
export type SubmitReviewResult = z.infer<typeof submitReviewResultSchema>;

/* ------------------------------------------------------------------ */
/* Tableau de bord                                                     */
/* ------------------------------------------------------------------ */

export const dashboardAlertCodeSchema = z.enum([
  'payment_failed',
  'trial_ended',
  'quota_exhausted',
  'smtp_not_configured',
  'smtp_failing',
  'queue_paused',
  'deliveries_failed',
  'sync_stale',
]);
export type DashboardAlertCode = z.infer<typeof dashboardAlertCodeSchema>;

/** Ce qu'il y a à corriger. Liste vide : tout fonctionne. */
export const dashboardAlertSchema = z.object({
  code: dashboardAlertCodeSchema,
  severity: z.enum(['error', 'warning']),
  count: z.number().int().optional(),
  detail: z.union([z.string(), z.number()]).optional(),
});
export type DashboardAlert = z.infer<typeof dashboardAlertSchema>;

export const dashboardSchema = z.object({
  alerts: z.array(dashboardAlertSchema),
  /** Étapes de mise en route du cabinet, dans l'ordre. */
  onboarding: z.array(
    z.object({
      key: z.enum(['doctolib', 'sync', 'smtp', 'message', 'reviews']),
      done: z.boolean(),
    }),
  ),
  /** Longueur de la période, en jours. */
  days: z.number().int(),
  /** Un `yyyy-mm-dd` par jour de la période, le plus ancien d'abord ; les séries suivent le même ordre. */
  labels: z.array(z.string()),
  lastSyncAt: z.iso.datetime().nullable(),
  messages: z.object({
    sent: z.number().int(),
    previousSent: z.number().int(),
    perDay: z.array(z.number()),
    scheduled: z.number().int(),
    byCategory: z.array(z.object({ catalogKey: z.string(), count: z.number().int() })),
  }),
  reviews: z.object({
    count: z.number().int(),
    previousCount: z.number().int(),
    average: z.number().nullable(),
    previousAverage: z.number().nullable(),
    redirected: z.number().int(),
    previousRedirected: z.number().int(),
    toHandle: z.number().int(),
    responseRate: z.number().nullable(),
    perDay: z.array(z.number()),
    redirectedPerDay: z.array(z.number()),
    /** Nombre d'avis à 1, 2, 3, 4 et 5 étoiles. */
    distribution: z.array(z.number()),
    recent: z.array(
      z.object({
        id: z.string(),
        patientName: z.string(),
        rating: z.number().int(),
        message: z.string(),
        createdAt: z.iso.datetime(),
        redirected: z.boolean(),
        status: reviewStatusSchema,
      }),
    ),
  }),
});
export type Dashboard = z.infer<typeof dashboardSchema>;

/** `GET /admin/overview` : chiffres clés du back-office, courbes et alertes à traiter. */
const overviewCabinetSchema = z.object({
  id: z.string(),
  nom: z.string(),
  userId: z.string().nullable(),
});
export const adminOverviewSchema = z.object({
  days: z.number().int(),
  labels: z.array(z.string()),
  cabinets: z.object({
    total: z.number().int(),
    essai: z.number().int(),
    actif: z.number().int(),
    impaye: z.number().int(),
    resilie: z.number().int(),
    gold: z.number().int(),
  }),
  signups: z.object({
    count: z.number().int(),
    previous: z.number().int(),
    perDay: z.array(z.number()),
  }),
  sends: z.object({
    sent: z.number().int(),
    previousSent: z.number().int(),
    failed: z.number().int(),
    sentPerDay: z.array(z.number()),
    failedPerDay: z.array(z.number()),
  }),
  trialsEndingSoon: z.array(overviewCabinetSchema.extend({ finEssai: z.iso.datetime() })),
  recentSignups: z.array(
    overviewCabinetSchema.extend({ statut: z.string(), createdAt: z.iso.datetime() }),
  ),
  alerts: z.array(
    z.object({
      level: z.enum(['critique', 'attention']),
      code: z.string(),
      title: z.string(),
      detail: z.string(),
      link: z.string().nullable(),
      cabinets: z.array(overviewCabinetSchema),
    }),
  ),
});
export type AdminOverview = z.infer<typeof adminOverviewSchema>;
